// BookTickets/stages/DateAndTime/DateAndTime.tsx
'use client'

import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import cn from 'classnames'
import { startOfDay, isPast, parseISO, format as formatDate } from 'date-fns'
import { useTranslations } from 'next-intl'
import posthog from 'posthog-js'

import styles from './index.module.scss'
import shared from '@booking/index.module.scss'

import type { Session } from '@booking/types/booking'

import BookingList from './components/BookingList'
import BookingCalendar from './components/BookingCalendar'

import SummaryBox from '@booking/components/SummaryBox'
import HelpBox from '@booking/components/HelpBox'
import BackButton from '@booking/components/BackButton'
import ContinueButton from '@booking/components/ContinueButton'

import ChevronLeft from './assets/chevron-left.svg'
import ChevronRight from './assets/chevron-right.svg'

import { useBookingActionsCtx, useBookingState } from '@booking/state/booking.context'
import { useBookingExternal } from '@booking/state/external.context'
import { resolveCalendarLocale } from './lib/dateAndTime'
import { usePersistedSelectedDate } from './hooks/usePersistedSelectedDate'
import { useSessionsForDate } from './hooks/useSessionsForDate'
import { SessionUnavailableError } from '@booking/lib/errors/availability'
import { presaleSingleDay } from '@booking/lib/presale'

export default function DateAndTime() {
  const t = useTranslations('booking')
  const { locale } = useBookingExternal()

  const state = useBookingState()
  const hasExtras = Boolean(state.config?.extras)
  const { dispatch, onCompleteDateTime } = useBookingActionsCtx()

  const leftColumnRef = useRef<HTMLDivElement | null>(null)

  const { selectedDate, setSelectedDate, initialised } = usePersistedSelectedDate()

  // A flow selling a single day skips the calendar and only asks for the time.
  const singleDay = presaleSingleDay(state.config)
  const singleDayDate = useMemo(() => (singleDay ? parseISO(singleDay) : null), [singleDay])

  const { loading, error, sessions, refresh } = useSessionsForDate({
    date: singleDayDate ?? selectedDate,
    enabled: initialised,
    sessionTags: state.config.sessionTags,
    config: state.config,
    selectedSessionId: state.session?.id,
  })

  // A pre-sale flow sells only its own dates, so its calendar offers nothing else.
  const presaleCalendar = useMemo(() => {
    const dates = state.config.presale?.sessionDates
    if (!dates?.length) return null
    const days = dates.map((d) => parseISO(d)).sort((a, b) => a.getTime() - b.getTime())
    return {
      minDate: days[0],
      maxDate: days[days.length - 1],
      isDateDisabled: (d: Date) => !dates.includes(formatDate(d, 'yyyy-MM-dd')),
    }
  }, [state.config.presale])

  const [showSessions, setShowSessions] = useState(true)
  const [unavailableNotice, setUnavailableNotice] = useState(false)

  const calendarLocale = useMemo(() => resolveCalendarLocale(locale), [locale])

  const scrollListIntoViewOnMobile = useCallback(() => {
    if (typeof window === 'undefined') return
    if (window.innerWidth >= 768) return
    if (!leftColumnRef.current) return

    const bounding = leftColumnRef.current.getBoundingClientRect()
    window.scrollTo({
      top: bounding.top + document.documentElement.scrollTop + bounding.height - window.innerHeight,
      behavior: 'smooth',
    })
  }, [])

  const handleDateChange = useCallback(
    (next: Date) => {
      // clear selection when date changes
      dispatch({ type: 'SET_SESSION', session: null })
      setUnavailableNotice(false)
      setSelectedDate(next)
      scrollListIntoViewOnMobile()
    },
    [dispatch, scrollListIntoViewOnMobile, setSelectedDate],
  )

  // Changing month also moves the selected date (to the 1st, or today), so the
  // session picked on the old date goes too, as it does for a day click.
  const onMonthChange = useCallback(
    (activeStartDate: Date | null) => {
      if (!activeStartDate) return
      dispatch({ type: 'SET_SESSION', session: null })
      setUnavailableNotice(false)
      if (isPast(startOfDay(activeStartDate))) {
        setSelectedDate(new Date())
        return
      }
      setSelectedDate(activeStartDate)
    },
    [dispatch, setSelectedDate],
  )

  const bookSession = useCallback(
    (s: Session) => {
      setUnavailableNotice(false)
      dispatch({ type: 'SET_SESSION', session: s })
      posthog.capture('session_selected', {
        session_id: s.id,
        session_datetime: s.dateTime,
        available_seats: s.vacancies,
        is_sold_out: s.vacancies === 0,
      })
    },
    [dispatch],
  )

  const onContinue = useCallback(async () => {
    if (!state.session) return

    try {
      await onCompleteDateTime()
    } catch (e) {
      // Sold out between selecting and submitting. Mark the session so the list
      // renders it as unavailable, drop the selection so Continue is disabled
      // until another session is picked, and tell the user why.
      if (e instanceof SessionUnavailableError) {
        dispatch({ type: 'MARK_SESSION_UNAVAILABLE', sessionId: e.sessionId })
        dispatch({ type: 'SET_SESSION', session: null })
        setUnavailableNotice(true)
        scrollListIntoViewOnMobile()
      }
      // Re-thrown either way so ContinueButton re-enables itself.
      throw e
    }
  }, [dispatch, onCompleteDateTime, scrollListIntoViewOnMobile, state.session])

  return (
    <div className={cn(shared.box, styles.wrapper)}>
      <div className={cn(shared.inner)}>
        <div className={cn(shared.leftColumn)} ref={leftColumnRef}>
          <BackButton />
          <h2 className={shared.title}>
            {t(
              singleDayDate
                ? 'ticket.selectDate.titleTime'
                : hasExtras
                  ? 'ticket.selectDate.titleAlt'
                  : 'ticket.selectDate.title',
            )}
          </h2>

          <div className={cn(styles.left)}>
            {unavailableNotice ? (
              <div
                className={cn(styles.unavailableNotice)}
                role="alert"
                data-id="session-unavailable"
              >
                {t('ticket.selectDate.sessionUnavailable')}
              </div>
            ) : null}

            {singleDayDate ? (
              <BookingList
                date={singleDayDate}
                loading={loading}
                error={error}
                sessions={sessions}
                onBook={bookSession}
                onRetry={refresh}
                variant="grid"
              />
            ) : (
              <div className={cn(styles.leftInner)}>
                <BookingCalendar
                  value={selectedDate}
                  locale={calendarLocale}
                  minDate={presaleCalendar?.minDate ?? new Date()}
                  maxDate={presaleCalendar?.maxDate}
                  isDateDisabled={presaleCalendar?.isDateDisabled}
                  onChange={handleDateChange}
                  onViewChange={(view) => setShowSessions(view === 'month')}
                  onMonthChange={onMonthChange}
                  prevLabel={<ChevronLeft />}
                  nextLabel={<ChevronRight />}
                  specialLabel={t('special')}
                  renderSpecialPopover={(label) => (
                    <div className={cn('popover', styles.popover)}>*{label}</div>
                  )}
                />

                {showSessions ? (
                  <div className={cn(styles.bookingList)}>
                    <BookingList
                      key={selectedDate.toISOString()}
                      date={selectedDate}
                      loading={loading}
                      error={error}
                      sessions={sessions}
                      onBook={bookSession}
                      onRetry={refresh}
                    />
                  </div>
                ) : (
                  <div className={cn(styles.bottomBorder)} />
                )}
              </div>
            )}
          </div>

          <ContinueButton className={cn(shared.continue)} onContinue={onContinue} />
        </div>

        <div className={cn(shared.rightColumn)}>
          <SummaryBox onContinue={onContinue} />
          <HelpBox />
        </div>
      </div>
    </div>
  )
}
